import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Partner, PartnersService } from 'src/app/services/partners/partners.service';
import {
  IBillingCycle,
  IProduct,
  ProductsSubprojectService,
  VendorProductWithSubprojectProduct,
} from 'src/app/services/products-subproject/products-subproject.service';
import {
  VendorProduct,
  VendorProductsService,
} from 'src/app/services/vendorproducts/vendorproducts.service';

@Component({
    selector: 'app-vendor-products-brand-list',
    templateUrl: './vendor-products-brand-list.component.html',
    styleUrls: ['./vendor-products-brand-list.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VendorProductsBrandListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  public columnNames = ['expand', 'logoUrl', 'name', 'redirectUrl'];
  public tableDataSource = new MatTableDataSource<Partner>();
  public brands: Partner[] = [];
  public products: VendorProductWithSubprojectProduct[] = [];
  public openedBrands: string[] = [];
  public billingCycles: IBillingCycle[] = [];

  constructor(
    private readonly partnersService: PartnersService,
    private readonly vendorProductsService: VendorProductsService,
    private readonly productsSubprojectService: ProductsSubprojectService,
    private readonly snackBar: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadPartners();
    await this.loadBillingCycles();
    await this.loadProducts();
    if (this.sort) this.tableDataSource.sort = this.sort;
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
  }

  public isBrandExpanded(brandId: string): boolean {
    return this.openedBrands.includes(brandId);
  }

  public toggleBrandExpansion(brand: Partner): void {
    const brandId = brand.partnerId as string;
    if (this.openedBrands.includes(brandId)) {
      this.openedBrands = this.openedBrands.filter((id) => id !== brandId);
    } else {
      this.openedBrands = [...this.openedBrands, brandId];
    }
  }

  private async loadPartners() {
    const partnersResponse = await this.partnersService.getAllPartners();
    this.tableDataSource.data = partnersResponse;
    this.brands = partnersResponse;
  }

  private async loadBillingCycles() {
    const billingCyclesResponse = await this.productsSubprojectService.getBillingCycles();
    if (billingCyclesResponse.success) {
      this.billingCycles = billingCyclesResponse.data;
    } else {
      this.snackBar.open('Failed to load billing cycles', 'Close', { duration: 3000 });
    }
  }

  private async loadProducts() {
    try {
      const [vendorProducts, activeProductsResponse] = await Promise.all([
        this.vendorProductsService.getAllVendorProducts(),
        this.productsSubprojectService.getActiveProducts(),
      ]);

      if (!activeProductsResponse.success) {
        console.error('Failed to load active products');
        return;
      }

      this.products = this.mapVendorProductsWithSubprojectIds(
        vendorProducts,
        activeProductsResponse.data,
      );
    } catch (error) {
      console.error('Error loading products:', error);
      this.snackBar.open('Failed to load products', 'Close', { duration: 3000 });
    }
  }

  private mapVendorProductsWithSubprojectIds(
    vendorProducts: VendorProduct[],
    activeStripeProducts: IProduct[],
  ): VendorProductWithSubprojectProduct[] {
    const stripeProductMap = this.createStripeProductMap(activeStripeProducts);

    return vendorProducts
      .filter((vp) => vp.stripeProductId && stripeProductMap.has(vp.stripeProductId))
      .map((vp) => ({
        ...vp,
        subprojectProductId: stripeProductMap.get(vp.stripeProductId as string)?.id,
      }));
  }

  private createStripeProductMap(activeStripeProducts: IProduct[]): Map<string, IProduct> {
    return new Map(activeStripeProducts.map((product) => [product.stripeProductId, product]));
  }
}
